import { Intersection } from "../Intersection.js";
import { errDiv } from "../../../util/mathHelpers.ts";

// const DEBUG = false;

export const dirMap = {
  NB: { Major1: "EB", Major2: "WB", Minor1: "NB", Minor2: "SB" },
  WB: { Major1: "NB", Major2: "SB", Minor1: "WB", Minor2: "EB" },
  EB: { Major1: "SB", Major2: "NB", Minor1: "EB", Minor2: "WB" },
  SB: { Major1: "WB", Major2: "EB", Minor1: "SB", Minor2: "NB" },
};

export function mappedZoneDefaultInputs(type, inputs) {
  const mappedInputs = {};

  for (const key in inputs) {
    if (dirMap[type].hasOwnProperty(key)) {
      mappedInputs[dirMap[type][key]] = inputs[key];
    }
  }

  return mappedInputs;
}

const revertMap = {
  NB: { EB: "Major1", WB: "Major2", NB: "Minor1", SB: "Minor2" },
  WB: { NB: "Major1", SB: "Major2", WB: "Minor1", EB: "Minor2" },
  EB: { SB: "Major1", NB: "Major2", EB: "Minor1", WB: "Minor2" },
  SB: { WB: "Major1", EB: "Major2", SB: "Minor1", NB: "Minor2" },
};

function revertZoneInputs(type, inputs) {
  const revertedInputs = {};

  for (const key in inputs) {
    if (revertMap[type].hasOwnProperty(key)) {
      revertedInputs[revertMap[type][key]] = inputs[key];
    }
  }

  return revertedInputs;
}
/** Continuous Green T computational class. Extends the Intersection parent class */
export class ContinuousGreenT extends Intersection {
  /**
   * Constructor for the ContinuousGreenT class.
   * @param {string} name - Name of the intersection
   * @param {Object} volumes - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects.
   * @param {Object} globalParams - Object containing the required global parameters for an intersection.
   * @param {string} stemDirection - String (NB, SB, EB, WB) identifying the "stem" leg.  E.g. if stemDirection = "NB", then no SB leg exists
   */
  constructor(
    name,
    volumes,
    globalParams,
    stemDirection = ContinuousGreenT.NB_STEM
  ) {
    super(name, volumes, globalParams);

    this.stemDirection = stemDirection;

    this.LaneConfig = {
      Major1: {
        TLanes: 1,
        RTLanes: 1,
        RTShared: false,
        RTChan: false,
      },
      Major2: {
        TLanes: 1,
        LTLanes: 1,
      },
      Minor1: {
        LTLanes: 1,
        RTLanes: 1,
        RTShared: false,
        RTChan: false,
      },
    };

    this.conflict = {
      countCrossing: 3,
      countMerging: 3,
      countDiverging: 3,
    };
  }

  /**
   * Function to get the DEFAULT inputs available for a specific zone.  This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * A continuous green t has a single zone (Z5) and is dependent on the specified "stem" direction.
   *
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    return {
      Z5: {
        Major1: {
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
        Major2: {
          T: 1,
          LT: 1,
        },
        Minor1: {
          LT: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
      },
    };
  }

  static getLaneConfigLabelsMapping() {
    return {
      Z5: this.getLabelsMappingWithoutMajorDir(),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    this.LaneConfig.Major1.TLanes = laneConfigInputs.Z5.Major1.T;
    this.LaneConfig.Major1.RTLanes = laneConfigInputs.Z5.Major1.RT;
    this.LaneConfig.Major1.RTShared = laneConfigInputs.Z5.Major1.RTShared;
    this.LaneConfig.Major1.RTChan = laneConfigInputs.Z5.Major1.RTChan;

    this.LaneConfig.Major2.LTLanes = laneConfigInputs.Z5.Major2.LT;
    this.LaneConfig.Major2.TLanes = laneConfigInputs.Z5.Major2.T;

    this.LaneConfig.Minor1.LTLanes = laneConfigInputs.Z5.Minor1.LT;
    this.LaneConfig.Minor1.RTLanes = laneConfigInputs.Z5.Minor1.RT;
    this.LaneConfig.Minor1.RTShared = laneConfigInputs.Z5.Minor1.RTShared;
    this.LaneConfig.Minor1.RTChan = laneConfigInputs.Z5.Minor1.RTChan;
  }

  // Implements the computeVCAnalysis function of the Intersection parent class.
  _runCriticalMovementAnalysis() {
    const volumes = {};
    switch (this.stemDirection) {
      default:
      case ContinuousGreenT.NB_STEM:
        volumes["Major1"] = {
          RT: this.EBR_MASTER,
          T: this.EBT_MASTER,
          LT: 0,
          dir: "EB",
        };
        volumes["Major2"] = {
          RT: 0,
          T: this.WBT_MASTER,
          LT: this.WBL_MASTER,
          dir: "WB",
        };
        volumes["Minor1"] = {
          RT: this.NBR_MASTER,
          T: 0,
          LT: this.NBL_MASTER,
          dir: "NB",
        };
        break;
      case ContinuousGreenT.SB_STEM:
        volumes["Major1"] = {
          RT: this.WBR_MASTER,
          T: this.WBT_MASTER,
          LT: 0,
          dir: "WB",
        };
        volumes["Major2"] = {
          RT: 0,
          T: this.EBT_MASTER,
          LT: this.EBL_MASTER,
          dir: "EB",
        };
        volumes["Minor1"] = {
          RT: this.SBR_MASTER,
          T: 0,
          LT: this.SBL_MASTER,
          dir: "SB",
        };
        break;
      case ContinuousGreenT.EB_STEM:
        volumes["Major1"] = {
          RT: this.SBR_MASTER,
          T: this.SBT_MASTER,
          LT: 0,
          dir: "SB",
        };
        volumes["Major2"] = {
          RT: 0,
          T: this.NBT_MASTER,
          LT: this.NBL_MASTER,
          dir: "NB",
        };
        volumes["Minor1"] = {
          RT: this.EBR_MASTER,
          T: 0,
          LT: this.EBL_MASTER,
          dir: "EB",
        };
        break;
      case ContinuousGreenT.WB_STEM:
        volumes["Major1"] = {
          RT: this.NBR_MASTER,
          T: this.NBT_MASTER,
          LT: 0,
          dir: "NB",
        };
        volumes["Major2"] = {
          RT: 0,
          T: this.SBT_MASTER,
          LT: this.SBL_MASTER,
          dir: "SB",
        };
        volumes["Minor1"] = {
          RT: this.WBR_MASTER,
          T: 0,
          LT: this.WBL_MASTER,
          dir: "WB",
        };
        break;
    }

    // Implement Critical Lane Volume Analysis
    const intx5 = {
      CLV: 0,
      VC: 0,
      Major1: { criticalVol: 0 },
      Major2: { criticalVol: 0 },
      Minor1: { criticalVol: 0 },
    };
    // ---- Determine Major 1 (Through and RT movements allowed) volumes based on lanes and shared/channelized inputs
    intx5.Major1.RT = 0; // Initialize to 0, value will hold if RT is channelized
    intx5.Major1.T = volumes.Major1.T;
    if (this.LaneConfig.Major1.RTChan === false) {
      intx5.Major1.RT = volumes.Major1.RT;
      if (this.LaneConfig.Major1.RTShared) {
        intx5.Major1.RT = Math.max(
          intx5.Major1.RT *
            (this.LaneConfig.Major1.RTLanes /
              (this.LaneConfig.Major1.RTLanes + 1))
        );
      }
      intx5.Major1.T += Math.round(
        (volumes.Major1.RT - intx5.Major1.RT) / this.RTAF
      );
    }
    // ---- Determine Major 2 (Through and LT movements allowed) volumes based on lanes and shared/channelized inputs
    intx5.Major2.T = volumes.Major2.T;
    intx5.Major2.LT = volumes.Major2.LT;
    // ---- Determine Minor "Stem" (LT and RT movements allowed) volumes based on lanes and shared/channelized inputs
    intx5.Minor1.LT = volumes.Minor1.LT;
    if (this.LaneConfig.Minor1.RTShared && this.LaneConfig.Minor1.LTLanes < 2) {
      intx5.Minor1.LT = Math.round(
        intx5.Minor1.LT *
          (this.LaneConfig.Minor1.LTLanes /
            (this.LaneConfig.Minor1.LTLanes + 1))
      );
    }
    intx5.Minor1.RT = 0; // Initialize to 0, value will hold if RT is channelized
    if (this.LaneConfig.Minor1.RTChan === false) {
      intx5.Minor1.RT = volumes.Minor1.RT;
      if (this.LaneConfig.Minor1.RTShared) {
        intx5.Minor1.RT = Math.round(
          intx5.Minor1.RT *
            (this.LaneConfig.Minor1.RTLanes /
              (this.LaneConfig.Minor1.RTLanes + 1))
        );
      }
    }
    intx5.Minor1.T = 0;
    if (this.LaneConfig.Minor1.RTShared) {
      let sharedLefts = (volumes.Minor1.LT - intx5.Minor1.LT) / this.LTAF;
      let sharedRights = (volumes.Minor1.RT - intx5.Minor1.RT) / this.RTAF;
      intx5.Minor1.T = Math.round(sharedLefts + sharedRights);
    }

    intx5.MajorSplit =
      this.LaneConfig.Major2.LTShared && this.LaneConfig.Major2.LTLanes > 0;
    intx5.MinorSplit = true;
    if (intx5.MajorSplit) {
      // MAX(IFERROR(Z5EBR/RTAF/Z5EBRlanes,0)-IFERROR(Z5NBL/LTAF/Z5NBLlanes,0),IFERROR(Z5EBT/Z5EBTlanes,0))
      let valMaj1A = errDiv(
        intx5.Major1.RT,
        this.RTAF,
        this.LaneConfig.Major1.RTLanes
      );
      let valMaj1B = errDiv(
        intx5.Minor1.LT,
        this.LTAF,
        this.LaneConfig.Minor1.LTLanes
      );
      let valMaj1C = errDiv(intx5.Major1.T, this.LaneConfig.Major1.TLanes);
      intx5.Major1.criticalVol = Math.max(valMaj1A - valMaj1B, valMaj1C);
      // IFERROR(Z5WBL/LTAF/Z5WBLlanes,0)
      intx5.Major2.criticalVol = errDiv(
        intx5.Major2.LT,
        this.LTAF,
        this.LaneConfig.Major2.LTLanes
      );
    } else {
      intx5.Major1.criticalVol = 0;
      // IFERROR(Z5WBL/LTAF/Z5WBLlanes,0) +MAX(IFERROR(Z5EBT/Z5EBTlanes,0), MAX(0,IFERROR(Z5EBR/RTAF/Z5EBRlanes-IFERROR(Z5NBL/LTAF/Z5NBLlanes,0),0)))
      let valMaj2A = errDiv(
        intx5.Major2.LT,
        this.LTAF,
        this.LaneConfig.Major2.LTLanes
      );
      let valMaj2B = errDiv(intx5.Major1.T, this.LaneConfig.Major1.TLanes);
      let valMaj2C = errDiv(
        intx5.Minor1.LT,
        this.RTAF,
        this.LaneConfig.Minor1.LTLanes
      );
      // NOTE: previous using  >=0 , changed to > 0 to avoid RTlanes = 0
      let valMaj2D =
        this.LaneConfig.Major1.RTLanes > 0
          ? intx5.Major1.RT / this.RTAF / this.LaneConfig.Major1.RTLanes -
            valMaj2C
          : 0;
      intx5.Major2.criticalVol =
        valMaj2A + Math.max(valMaj2B, Math.max(0, valMaj2D));
    }
    // =ROUND(MAX(IFERROR(Z5NBL/LTAF/Z5NBLlanes,0),Z5NBT/1,IFERROR(Z5NBR/RTAF/Z5NBRlanes,0)-IFERROR(Z5WBL/LTAF/Z5WBLlanes,0)),0)
    let valMinor1A = errDiv(
      intx5.Minor1.LT,
      this.LTAF,
      this.LaneConfig.Minor1.LTLanes
    );
    let valMinor1B = intx5.Minor1.T / 1;
    let valMinor1C =
      errDiv(intx5.Minor1.RT, this.RTAF, this.LaneConfig.Minor1.RTLanes) -
      errDiv(intx5.Major2.LT, this.LTAF, this.LaneConfig.Major2.LTLanes);
    intx5.Minor1.criticalVol = Math.max(valMinor1A, valMinor1B, valMinor1C);
    // Round all critical volumes
    intx5.Major1.criticalVol = Math.round(intx5.Major1.criticalVol);
    intx5.Major2.criticalVol = Math.round(intx5.Major2.criticalVol);
    intx5.Minor1.criticalVol = Math.round(intx5.Minor1.criticalVol);

    intx5.CLV =
      intx5.Minor1.criticalVol +
      (intx5.MajorSplit
        ? intx5.Major1.criticalVol + intx5.Major2.criticalVol
        : Math.max(intx5.Major1.criticalVol, intx5.Major2.criticalVol));
    intx5.VC = intx5.CLV / this.CLV_Limit_3;

    // if (DEBUG) {
    //   console.log(
    //     "Major 1 L/T/R (" +
    //       volumes.Major1.dir +
    //       "): 0, " +
    //       intx5.Major1.T +
    //       ", " +
    //       intx5.Major1.RT
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     "Major 2 L/T/R (" +
    //       volumes.Major2.dir +
    //       "): " +
    //       intx5.Major2.LT +
    //       ", " +
    //       intx5.Major2.T +
    //       ", 0"
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     "Minor Stem L/T/R (" +
    //       volumes.Minor1.dir +
    //       "): " +
    //       intx5.Minor1.LT +
    //       ", " +
    //       intx5.Minor1.T +
    //       ", " +
    //       intx5.Minor1.RT
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     "Major 1 CritVol (" +
    //       volumes.Major1.dir +
    //       "): " +
    //       intx5.Major1.criticalVol
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     "Major 2 CritVol (" +
    //       volumes.Major2.dir +
    //       "): " +
    //       intx5.Major2.criticalVol
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     "Minor Stem CritVol (" +
    //       volumes.Minor1.dir +
    //       "): " +
    //       intx5.Minor1.criticalVol
    //   );
    // }
    // if (DEBUG) {
    //   console.log("Zone 5 CLV: " + intx5.CLV);
    // }
    // if (DEBUG) {
    //   console.log("Zone 5 V/C: " + intx5.VC);
    // }

    // let continuousMovementCLV = Math.round(
    //   volumes.Major2.T / this.LaneConfig.Major2.TLanes
    // );
    // let continuousMovementVC = continuousMovementCLV / this.CLV_Limit_3;
    // Assign results for each zone
    this._resultsByZone = {
      Z5: {
        VC: intx5.VC,
        CLV: intx5.CLV,
      },
      // continuousMovement is not shown in capX
      // Z6: {
      //   VC: continuousMovementVC,
      //   CLV: continuousMovementCLV,
      // },
    };
  }

  getAccommodation() {
    return "-";
  }

  static get NB_STEM() {
    return "NB";
  }
  static get SB_STEM() {
    return "SB";
  }
  static get EB_STEM() {
    return "EB";
  }
  static get WB_STEM() {
    return "WB";
  }
}

export class ContinuousGreenTNB extends ContinuousGreenT {
  constructor(name, volumes, globalParams) {
    super(name, volumes, globalParams, ContinuousGreenT.NB_STEM);
  }

  static getZoneDefaultInputs() {
    const defaultInputs = ContinuousGreenT.getZoneDefaultInputs();
    return {
      Z5: mappedZoneDefaultInputs(ContinuousGreenT.NB_STEM, defaultInputs.Z5),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    super.setLaneConfigInputs({
      Z5: revertZoneInputs(ContinuousGreenT.NB_STEM, laneConfigInputs.Z5),
    });
  }
}

export class ContinuousGreenTSB extends ContinuousGreenT {
  constructor(name, volumes, globalParams) {
    super(name, volumes, globalParams, ContinuousGreenT.SB_STEM);
  }

  static getZoneDefaultInputs() {
    const defaultInputs = ContinuousGreenT.getZoneDefaultInputs();
    return {
      Z5: mappedZoneDefaultInputs(ContinuousGreenT.SB_STEM, defaultInputs.Z5),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    super.setLaneConfigInputs({
      Z5: revertZoneInputs(ContinuousGreenT.SB_STEM, laneConfigInputs.Z5),
    });
  }
}

export class ContinuousGreenTEB extends ContinuousGreenT {
  constructor(name, volumes, globalParams) {
    super(name, volumes, globalParams, ContinuousGreenT.EB_STEM);
  }

  static getZoneDefaultInputs() {
    const defaultInputs = ContinuousGreenT.getZoneDefaultInputs();
    return {
      Z5: mappedZoneDefaultInputs(ContinuousGreenT.EB_STEM, defaultInputs.Z5),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    super.setLaneConfigInputs({
      Z5: revertZoneInputs(ContinuousGreenT.EB_STEM, laneConfigInputs.Z5),
    });
  }
}

export class ContinuousGreenTWB extends ContinuousGreenT {
  constructor(name, volumes, globalParams) {
    super(name, volumes, globalParams, ContinuousGreenT.WB_STEM);
  }

  static getZoneDefaultInputs() {
    const defaultInputs = ContinuousGreenT.getZoneDefaultInputs();
    return {
      Z5: mappedZoneDefaultInputs(ContinuousGreenT.WB_STEM, defaultInputs.Z5),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    super.setLaneConfigInputs({
      Z5: revertZoneInputs(ContinuousGreenT.WB_STEM, laneConfigInputs.Z5),
    });
  }
}
